<script>
    // Interactive coachmark tour. Spotlights real UI elements, dims the rest,
    // one short line per step. Progress is persisted; navigation is client-side
    // via goto so the tour stays mounted while it walks between pages.
    import { onMount, onDestroy, tick } from "svelte";
    import { fade } from "svelte/transition";
    import { ArrowRightOutline, ArrowLeftOutline, CloseOutline } from "flowbite-svelte-icons";
    import { page } from "$app/stores";
    import { goto } from "$app/navigation";
    import {
        tour_active,
        tour_step,
        authenticated_user,
        markOnboardingSeen,
    } from "../../service/store";
    import { STEPS, SLUG_LABEL } from "./tourSteps";


    $: currentSlug = $page.url.pathname.split("/").filter(Boolean).pop() ?? "";
    let rect = null;
    let vw = 0;
    let vh = 0;
    let pollTimer;
    let scrolling = false; // a step-entry scroll is in flight
    let readyIdx = -1; // step whose card is prepared and safe to show
    let prepGen = 0; // guards against overlapping prepareStep() runs
    let prepKey = "";

    // How long to wait for an anchor that renders late (the More dropdown, or a
    // page that has just navigated) before accepting that it isn't coming.
    const ANCHOR_WAIT = 700;
    // Smooth-scrolling a long page takes a while. Wait for it to stop before
    // showing the card, otherwise the card drifts along with the target.
    const SCROLL_SETTLE = 1200;
    // Only scroll when the target is awkwardly placed; a target already in
    // comfortable view is left alone rather than nudged for no reason.
    const COMFORT_TOP = 0.15;
    const COMFORT_BOTTOM = 0.85;

    $: idx = $tour_step;
    $: step = STEPS[idx] ?? null;
    $: total = STEPS.length;
    $: isFirst = idx === 0;
    $: isLast = idx === total - 1;
    // No later section => skipping just runs to the sign-off card, so "Skip".
    $: hasLaterSection = !!step && STEPS.slice(idx + 1).some((s) => s.kicker !== step.kicker);
    $: needsNav = !!(step && step.slug && step.slug !== currentSlug);
    $: expectsAnchor = !!(step && !step.center && !needsNav && step.sel);
    $: anchored = !!(rect && expectsAnchor);
    // While the page is scrolling to the target, `rect` is a moving number, so
    // drawing the spotlight from it makes it chase the page. Dim everything
    // instead and cut the hole once the target has come to rest.
    $: showSpotlight = anchored && !scrolling;
    // The card stays hidden until the step is fully prepared — anchor rendered,
    // dropdown open, scrolling finished — so it never appears and then moves.
    $: pendingReveal = readyIdx !== idx;

    // The dropdown toggles on click, so opening it means clicking the trigger —
    // and a second click shuts it again. Never poll this: mid-open the item's
    // height is still 0, so a "is it open yet?" check would read as closed and
    // click again, flickering the menu. `menuIdx` pins it to one click per step,
    // however often the reactive block re-runs.
    let menuIdx = -1;

    function moreMenuIsOpen() {
        const item = document.querySelector('[data-tour="more-notify"]');
        return !!(item && item.getBoundingClientRect().height > 0);
    }

    function clickMoreTrigger() {
        const btn = document.getElementById("more-dropdown-btn");
        if (btn) btn.click();
    }

    function openMoreMenuFor(i) {
        if (typeof document === "undefined") return;
        if (menuIdx === i) return;
        menuIdx = i;
        if (!moreMenuIsOpen()) clickMoreTrigger();
    }

    function closeMoreMenu() {
        if (typeof document === "undefined") return;
        menuIdx = -1;
        if (moreMenuIsOpen()) clickMoreTrigger();
    }

    // Flowbite's Popper closes the dropdown on any click outside it, via a
    // listener on document. The click that advances the step is still being
    // dispatched when we get here, so opening the menu synchronously would open
    // it and have that same click shut it again. Yield to a macrotask first, so
    // the event has finished bubbling before we touch the trigger.
    const afterCurrentEvent = () => new Promise((r) => setTimeout(r, 0));

    // Resolves once `sel` is actually laid out, so the card can be revealed on
    // the frame the dropdown finishes opening rather than on the next poll tick.
    function waitForAnchor(sel, timeout) {
        return new Promise((resolve) => {
            const started = Date.now();
            (function check() {
                const el = document.querySelector(sel);
                const r = el ? el.getBoundingClientRect() : null;
                if (r && r.width > 0 && r.height > 0) return resolve(true);
                if (Date.now() - started > timeout) return resolve(false);
                requestAnimationFrame(check);
            })();
        });
    }

    // Resolves once the target stops moving, i.e. smooth scrolling has finished,
    // so the card can be placed against a position that will not shift.
    function settleScroll(sel, timeout) {
        return new Promise((resolve) => {
            const started = Date.now();
            let lastTop = null;
            let stableFrames = 0;
            (function check() {
                const el = document.querySelector(sel);
                const r = el ? el.getBoundingClientRect() : null;
                if (!r) return resolve(false);
                const top = Math.round(r.top);
                stableFrames = top === lastTop ? stableFrames + 1 : 0;
                lastTop = top;
                if (stableFrames >= 3) return resolve(true);
                if (Date.now() - started > timeout) return resolve(false);
                requestAnimationFrame(check);
            })();
        });
    }

    function measure() {
        if (typeof window === "undefined") return;
        vw = window.innerWidth;
        vh = window.innerHeight;
        if (!$tour_active || !step || step.center || needsNav || !step.sel) {
            rect = null;
            return;
        }
        const el = document.querySelector(step.sel);
        const r = el ? el.getBoundingClientRect() : null;
        if (r && r.width > 0 && r.height > 0) {
            rect = { top: r.top, left: r.left, width: r.width, height: r.height, cx: r.left + r.width / 2 };
        } else if (readyIdx === idx) {
            // The anchor was there and has gone: fall back to a centred card.
            rect = null;
        }
        // Otherwise hold the previous rect while the step is still being
        // prepared, so the spotlight doesn't blink out in the meantime.
    }

    // Get everything into place *before* the card is shown: open the dropdown if
    // the step needs one, wait for the anchor to render, scroll it into view and
    // let that scroll finish. Only then reveal. Each stage can be superseded by a
    // newer step, hence the generation check after every await.
    async function prepareStep() {
        const gen = ++prepGen;
        scrolling = false; // this run owns the flag now
        await tick();
        if (gen !== prepGen || !$tour_active || !step) return;

        if (step.menu) {
            // Flowbite closes the dropdown on the very click that got us here,
            // so let that event finish before opening it.
            await afterCurrentEvent();
            if (gen !== prepGen) return;
            openMoreMenuFor(idx);
        } else {
            closeMoreMenu();
        }

        // Centred cards and "let's open X" prompts have nothing to wait for.
        if (!expectsAnchor) {
            measure();
            readyIdx = idx;
            return;
        }

        await waitForAnchor(step.sel, ANCHOR_WAIT);
        if (gen !== prepGen) return;

        const el = document.querySelector(step.sel);
        const r = el ? el.getBoundingClientRect() : null;
        if (el && r && el.scrollIntoView) {
            const centerY = r.top + r.height / 2;
            const h = window.innerHeight || 0;
            if (centerY < h * COMFORT_TOP || centerY > h * COMFORT_BOTTOM) {
                scrolling = true;
                el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
                await settleScroll(step.sel, SCROLL_SETTLE);
                // A newer run has taken over the flag — leave it to that run.
                if (gen !== prepGen) return;
                scrolling = false;
            }
        }

        measure();
        readyIdx = idx;
    }

    // Re-prepare whenever the step or the page changes, and when the tour opens.
    $: prepKeyNow = $tour_active ? idx + "|" + currentSlug : "";
    $: if (prepKeyNow !== prepKey) {
        prepKey = prepKeyNow;
        readyIdx = -1;
        if ($tour_active) prepareStep();
    }

    function close() {
        closeMoreMenu();
        markOnboardingSeen($authenticated_user?.id);
        tour_active.set(false);
        tour_step.set(0);
    }
    function gotoIndex(i) {
        if (i < 0 || i >= total) {
            close();
            return;
        }
        // Menu and scroll handling live in prepareStep: doing them here would
        // race with the click that triggered this navigation.
        tour_step.set(i);
        const s = STEPS[i];
        if (s.slug && s.slug !== currentSlug) {
            goto("/" + s.slug);
        }
    }
    function next() {
        if (isLast) close();
        else gotoIndex(idx + 1);
    }
    function prev() {
        if (!isFirst) gotoIndex(idx - 1);
    }
    function continueHere() {
        if (step?.slug) goto("/" + step.slug);
    }
    // Skip the rest of the current section (jump to the next section's first step).
    // In the final section there is no next section, so land on the sign-off card
    // rather than dropping the user out of the tour entirely.
    function skipSection() {
        for (let j = idx + 1; j < total; j++) {
            if (STEPS[j].kicker !== step.kicker) {
                gotoIndex(j);
                return;
            }
        }
        if (!isLast) gotoIndex(total - 1);
        else close();
    }

    onMount(() => {
        pollTimer = setInterval(measure, 200);
    });
    onDestroy(() => clearInterval(pollTimer));

    const PAD = 8;
    const TT_W = 300;
    const GAP = 14;

    $: spot = rect
        ? { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }
        : null;
    $: placeBelow = rect ? rect.top + rect.height / 2 < vh / 2 : true;
    $: tipTop = rect ? (placeBelow ? rect.top + rect.height + PAD + GAP : rect.top - PAD - GAP) : 0;
    $: tipLeft = rect ? Math.max(PAD, Math.min(rect.cx - TT_W / 2, vw - TT_W - PAD)) : 0;
    $: arrowLeft = rect ? Math.max(16, Math.min(rect.cx - tipLeft, TT_W - 16)) : TT_W / 2;
    $: tipStyle = anchored
        ? `left:${tipLeft}px;top:${tipTop}px;${placeBelow ? "" : "transform:translateY(-100%);"}`
        : "left:50%;top:50%;transform:translate(-50%,-50%);";
</script>

{#if $tour_active && step}
    <div class="fixed inset-0 z-[95] pointer-events-none" transition:fade={{ duration: 150 }}>
        {#if showSpotlight}
            <div
                class="absolute rounded-xl transition-all duration-200"
                style="top:{spot.top}px;left:{spot.left}px;width:{spot.width}px;height:{spot.height}px;box-shadow:0 0 0 9999px rgba(5,12,59,0.55);"
            ></div>
        {:else}
            <div class="absolute inset-0" style="background:rgba(5,12,59,0.55);"></div>
        {/if}

        <div
            class="absolute pointer-events-auto w-[300px] max-w-[calc(100vw-1rem)] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-card-border dark:border-gray-700 p-4 transition-opacity duration-150"
            class:opacity-0={pendingReveal}
            style={tipStyle}
            role="dialog"
            aria-modal="true"
            aria-label="SmartClaim guided tour"
        >
            {#if anchored}
                {#if placeBelow}
                    <div class="absolute w-0 h-0 border-x-8 border-x-transparent border-b-8 border-b-white dark:border-b-gray-800" style="top:-8px;left:{arrowLeft}px;transform:translateX(-50%);"></div>
                {:else}
                    <div class="absolute w-0 h-0 border-x-8 border-x-transparent border-t-8 border-t-white dark:border-t-gray-800" style="bottom:-8px;left:{arrowLeft}px;transform:translateX(-50%);"></div>
                {/if}
            {/if}

            <button
                class="absolute top-2.5 right-2.5 inline-flex items-center justify-center w-6 h-6 rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close tour"
                title="Close tour"
                on:click={close}
            >
                <CloseOutline class="w-3.5 h-3.5" />
            </button>

            {#if step.intro}
                <!-- Section intro: section name as the big heading, description as plain text -->
                <span class="text-base font-semibold uppercase tracking-wider text-[#5266eb] dark:text-blue-400 pr-6 block">{step.kicker}</span>
                <p class="text-[13px] text-gray-600 dark:text-gray-300 leading-snug mt-1.5">{step.title}</p>
            {:else}
                <span class="text-[11px] font-semibold uppercase tracking-wider text-[#5266eb] dark:text-blue-400 pr-6 block">{step.kicker}</span>
                <h3 class="text-[15px] font-semibold text-navy dark:text-white leading-snug mt-1">{step.title}</h3>
                {#if needsNav || step.body}
                    <p class="text-[13px] text-gray-600 dark:text-gray-300 leading-snug mt-1">
                        {needsNav ? `Let's open ${SLUG_LABEL[step.slug] ?? step.slug} to continue` : step.body}
                    </p>
                {/if}
            {/if}

            <div class="flex items-center justify-between mt-3.5">
                {#if isFirst}
                    <button class="text-[11px] font-medium text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors" on:click={close}>No thanks</button>
                {:else if isLast}
                    <!-- Sign-off card: nothing to skip. Spacer keeps Done on the right. -->
                    <span></span>
                {:else}
                    <button class="text-[11px] font-medium text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors" on:click={skipSection}>{hasLaterSection ? "Skip " + step.kicker : "Skip"}</button>
                {/if}
                <div class="flex items-center gap-1.5">
                    {#if !isFirst}
                        <button class="inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-600 dark:text-gray-300 border border-[#d8d8da] dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" aria-label="Back" on:click={prev}>
                            <ArrowLeftOutline class="w-3.5 h-3.5" />
                        </button>
                    {/if}
                    <button
                        class="inline-flex items-center gap-1 h-7 px-3.5 rounded-full text-[13px] font-medium !bg-[#050c3b] dark:!bg-white !text-white dark:!text-[#050c3b] hover:!bg-[#1a2070] dark:hover:!bg-gray-200 transition-colors"
                        on:click={needsNav ? continueHere : next}
                    >
                        {isLast ? "Done" : needsNav ? "Continue" : "Next"}
                        {#if !isLast && !needsNav}<ArrowRightOutline class="w-3.5 h-3.5" />{/if}
                    </button>
                </div>
            </div>
        </div>
    </div>
{/if}
